import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "sonner";
import addImage from "../../../assets/icons/addImage.svg";
import ImageCropperDialog from "../../ImageCropper";

const MAX_IMAGES = 4;

const MultiImageUploader = ({ label, images, setImages, centerAdd = false }) => {
  const [cropOpen, setCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const inputRef = React.useRef(null);


  /* ---------- FILE SELECT ---------- */
  const handleSelect = (e) => {
    const files = Array.from(e.target.files || []);

    if (images.length + files.length > MAX_IMAGES) {
      toast.error("Maximum 4 images allowed");
      return;
    }

    if (!files.length) return;
    e.target.value = "";

    // queue files → crop one by one
    setPendingFiles(files);
    openNextCrop(files);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const openNextCrop = (files) => {
    const file = files[0];
    const reader = new FileReader();

    reader.onload = () => {
      setImageSrc(reader.result);
      setCropOpen(true);
    };

    reader.readAsDataURL(file);
  };

  /* ---------- AFTER CROP ---------- */
  const handleCroppedImage = (croppedFile) => {
    if (!croppedFile) return;

    const preview = URL.createObjectURL(croppedFile);

    setImages((prev) => [...prev, { file: croppedFile, preview }]);

    const remaining = pendingFiles.slice(1);
    setPendingFiles(remaining);

    if (remaining.length) {
      openNextCrop(remaining);
    } else {
      setCropOpen(false);
      setImageSrc(null);
    }
  };

  /* ---------- REMOVE ---------- */
  const removeImage = (index) => {
    setImages((prev) => {
      const img = prev[index];
      if (img?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(img.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  /* ---------- CLEANUP ---------- */
  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (img.preview?.startsWith("blob:")) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, [images]);

  const isEmpty = images.length === 0;

  return (
    <Box>
      <Typography sx={{ fontSize: 16, fontWeight: 500, color: "var(--app-text-muted)", mb: 1 }}>
        {label}
      </Typography>

      {/* EMPTY STATE */}
      {isEmpty && (
        <Box
          component="label"
          sx={{
            width: centerAdd ? "40%" : "100%",
            height: centerAdd ? 100 : 120,
            borderRadius: 3,
            border: "1px dashed #5B5AFB",
            backgroundColor: "var(--app-surface-2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexDirection: "column",
            textAlign: "center",
          }}
        >
          <Box sx={{ mb: 1 }}>
            <img src={addImage} alt="addImage" />
          </Box>

          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            {centerAdd ? "Add Images" : "Add Image"}
          </Typography>

          <input
            ref={inputRef}
            hidden
            multiple
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleSelect}
          />
        </Box>
      )}

      {/* IMAGE GRID */}
      {!isEmpty && (
        <Box display="flex" gap={2} flexWrap="wrap">
          {images.map((img, i) => (
            <Box
              key={i}
              sx={{
                width: centerAdd ? 100 : 120,
                height: centerAdd ? 100 : 120,
                borderRadius: 3,
                border: "1px solid var(--app-border)",
                position: "relative",
                overflow: "hidden",
                backgroundColor: "var(--app-surface)",
              }}
            >
              <img
                src={img.preview}
                alt="preview"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />

              <IconButton
                size="small"
                onClick={() => removeImage(i)}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  backgroundColor: "var(--app-text)",
                  color: "#fff",
                  width: 22,
                  height: 22,
                  "&:hover": { backgroundColor: "var(--app-text)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}

          {/* ADD TILE */}
          {images.length < MAX_IMAGES && (
            <Box
              component="label"
              sx={{
                width: centerAdd ? 100 : 120,
                height: centerAdd ? 100 : 120,
                borderRadius: 3,
                border: "1px dashed #5B5AFB",
                backgroundColor: "#F5F6FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexDirection: "column",
              }}
            >
              <Box sx={{ mb: 0.5 }}>
                <img src={addImage} alt="addImage" />
              </Box>

              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                Add Image
              </Typography>

              <input
                hidden
                multiple
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleSelect}
              />
            </Box>
          )}
        </Box>
      )}

      <ImageCropperDialog
        open={cropOpen}
        imageSrc={imageSrc}
        aspect={1}
        onClose={() => setCropOpen(false)}
        onSave={handleCroppedImage}
      />
    </Box>
  );
};

export default MultiImageUploader;
